/** A world point (body segments, arm joints, ...). */
export interface BossPoint {
  x: number;
  y: number;
}

/** A part's hit circle — explosion damage numbers anchor on its top edge. */
export interface HitCircle extends BossPoint {
  r: number;
}

/** Point `at` at a hit circle in place (no allocation mid-fight). */
export function setCircle(at: HitCircle, x: number, y: number, r: number): void {
  at.x = x;
  at.y = y;
  at.r = r;
}

/**
 * Feedback recipe for one bespoke boss event: blast size ('big' = the
 * chain explosion), screen shake, explosion sfx volume and a yellow gem
 * burst. 0 skips that channel. Values live in config/boss-feedback.ts.
 */
export interface BurstFx {
  readonly blast: number | 'big';
  readonly shake: number;
  readonly sfx: number;
  readonly gems: number;
}

/** Recipe for a dying body: every 2nd point blasts, gems drop at `gemsAt`. */
export interface ChainFx {
  readonly blast: number;
  readonly shake: number;
  readonly sfx: number;
  readonly gems: number;
  readonly gemsAt: 'mid' | 'tip';
}

/**
 * Host side of a boss's bespoke events (wired once per fight by
 * combat/boss-hooks.ts). Bosses report WHAT happened — a part died, the
 * ground was slammed — and the host decides how it looks, sounds and pays.
 * Replaces the per-class `instanceof` callback wiring that lived in game.ts.
 */
export interface BossEvents {
  /** One feedback burst at a point (part death, split, slam). */
  burst(x: number, y: number, fx: BurstFx): void;
  /** Feedback along a dying body (Serpent snake, Kraken arm). */
  chain(points: ReadonlyArray<BossPoint>, fx: ChainFx): void;
  /**
   * Nearest obstacle-free spot to a normalized arena point (0..1), at least
   * `away` px from (avoidX, avoidY) — summoned parts never land on a rock.
   * Optional: headless hosts omit it and the boss keeps the raw spot.
   */
  clearPoint?(
    nx: number, ny: number, avoidX: number, avoidY: number, away: number, radius: number,
  ): BossPoint;
}
